/**
 * Bolha flutuante de "Solicitar atendimento" para o cliente.
 * - Aparece em todas as telas (Home, Pagamentos, Débitos, Moto, Perfil)
 * - Badge vermelho com número de mensagens não-lidas (poll 15s)
 * - Clique abre tela de atendimento
 */
import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle } from 'lucide-react';

const POLL_MS = 15000;

const AtendimentoFloatingButton = ({ api, onAbrir, visible = true }) => {
  const [naoLidas, setNaoLidas] = useState(0);
  const [statusAt, setStatusAt] = useState(null);

  const consultar = useCallback(async () => {
    try {
      const { data } = await api.get('/cliente/atendimento/mensagens');
      if (data?.atendimento) {
        setNaoLidas(data.atendimento.mensagens_nao_lidas_cliente || 0);
        setStatusAt(data.atendimento.status);
      } else {
        setNaoLidas(0);
        setStatusAt(null);
      }
    } catch {/* silent */}
  }, [api]);

  useEffect(() => {
    if (!visible) return;
    consultar();
    const id = setInterval(consultar, POLL_MS);
    return () => clearInterval(id);
  }, [consultar, visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed right-4 z-40"
      style={{
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 12px)', // sobe acima da bottom-nav
      }}
      data-testid="cliente-fab-atendimento-container"
    >
      <button
        onClick={onAbrir}
        className="relative rounded-full p-3.5 transition-transform hover:scale-105 active:scale-95"
        style={{
          background: 'linear-gradient(135deg, #00FF66 0%, #00D955 100%)',
          color: '#060A14',
          boxShadow: '0 8px 24px rgba(0, 255, 102, 0.45), 0 0 0 1px rgba(0, 255, 102, 0.3)',
          animation: naoLidas > 0 ? 'pulse-emerald 2s ease-in-out infinite' : undefined,
        }}
        aria-label="Solicitar atendimento"
        data-testid="cliente-fab-atendimento"
      >
        <MessageCircle className="w-6 h-6" strokeWidth={2.5} />
        {naoLidas > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
            style={{ boxShadow: '0 2px 6px rgba(239, 68, 68, 0.5)' }}
            data-testid="cliente-fab-badge-naolidas"
          >
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
        {/* Indicador visual de status (verde piscando se aberto) */}
        {statusAt && naoLidas === 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-300 border-2 border-slate-900"
            style={{ animation: 'pulse 2s ease-in-out infinite' }}
          />
        )}
      </button>

      {/* keyframes injetados inline para o pulse verde */}
      <style>{`
        @keyframes pulse-emerald {
          0%, 100% { box-shadow: 0 8px 24px rgba(0, 255, 102, 0.45), 0 0 0 0 rgba(0, 255, 102, 0.7); }
          50%      { box-shadow: 0 8px 24px rgba(0, 255, 102, 0.45), 0 0 0 10px rgba(0, 255, 102, 0); }
        }
      `}</style>
    </div>
  );
};

export default AtendimentoFloatingButton;
